import { useCallback, useMemo } from "react";
import type { UnifiedTableColumn } from "@/types/table-column";
import { useOrganizationSessionStorage } from "./use-browser-storage";

/**
 * Hook specifically for managing AI fields column selections.
 *
 * @param coreColumns - The core column definitions for AI fields
 * @returns Methods and state for managing selected AI fields columns
 */
export function useAIFieldsColumnSelection<T = Record<string, unknown>>(
  coreColumns: UnifiedTableColumn<T>[],
) {
  const [columnVisibility, setColumnVisibility] = useOrganizationSessionStorage<
    Record<string, boolean>
  >("ai-fields::list::column-visibility", {});

  const [columnOrder, setColumnOrder] = useOrganizationSessionStorage<string[]>(
    "ai-fields::list::column-order",
    coreColumns.map((col) => col.key as string),
  );

  // Initialize visibility for new columns
  const initializedVisibility = useMemo(() => {
    const visibility = { ...columnVisibility };
    coreColumns.forEach((col) => {
      const colId = col.key as string;
      if (!(colId in visibility)) {
        visibility[colId] = !col.exportOnly;
      }
      // Always show required columns
      if (col.required) {
        visibility[colId] = true;
      }
    });
    return visibility;
  }, [coreColumns, columnVisibility]);

  // Get selected columns
  const selectedColumns = useMemo(() => {
    return coreColumns.filter((col) => {
      const colId = col.key as string;
      return initializedVisibility[colId] === true;
    });
  }, [coreColumns, initializedVisibility]);

  // Toggle column visibility
  const toggleColumn = useCallback(
    (columnId: string, selected: boolean) => {
      const column = coreColumns.find(
        (col) => (col.key as string) === columnId,
      );
      if (column?.required && !selected) {
        return; // Don't allow hiding required columns
      }

      setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: selected,
      }));
    },
    [coreColumns, setColumnVisibility],
  );

  // Reset columns to default
  const resetColumns = useCallback(() => {
    const defaultVisibility: Record<string, boolean> = {};
    const defaultOrder: string[] = [];

    coreColumns.forEach((col) => {
      const colId = col.key as string;
      defaultVisibility[colId] = !col.exportOnly;
      defaultOrder.push(colId);
    });

    setColumnVisibility(defaultVisibility);
    setColumnOrder(defaultOrder);
  }, [coreColumns, setColumnVisibility, setColumnOrder]);

  return {
    selectedColumns,
    columnOrder,
    setColumnOrder,
    toggleColumn,
    resetColumns,
  };
}
